"use client";

import { useSyncExternalStore } from "react";

/**
 * Cookie-consent store — a dependency-free module store read through
 * `useSyncExternalStore`. State is persisted to `localStorage` (full state)
 * and to a `globalerp_consent` cookie (compact, 365d) so it survives sessions.
 *
 * `essential` is always true and cannot be refused. Consent decisions are
 * mirrored to the server (fire-and-forget) via /api/lgpd/consent.
 */

export interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export interface ConsentState {
  consentId: string | null;
  preferences: ConsentPreferences;
  policyVersion: string | null;
  consentDate: string | null;
  hasDecided: boolean;
  hydrated: boolean;
}

export type ConsentMethod = "banner" | "modal" | "settings";

const LS_KEY = "globalerp-consent";
const COOKIE_KEY = "globalerp_consent";
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

const ALL_OFF: ConsentPreferences = { essential: true, analytics: false, marketing: false, preferences: false };
const ALL_ON: ConsentPreferences = { essential: true, analytics: true, marketing: true, preferences: true };

const SERVER_STATE: ConsentState = {
  consentId: null,
  preferences: ALL_OFF,
  policyVersion: null,
  consentDate: null,
  hasDecided: false,
  hydrated: false,
};

let state: ConsentState = SERVER_STATE;
let hydrationDone = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function genId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "c-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readLocalStorage(): ConsentState | null {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<ConsentState>;
    if (!p || !p.consentId) return null;
    return {
      consentId: p.consentId,
      preferences: { ...ALL_OFF, ...(p.preferences ?? {}), essential: true },
      policyVersion: p.policyVersion ?? null,
      consentDate: p.consentDate ?? null,
      hasDecided: !!p.hasDecided,
      hydrated: true,
    };
  } catch {
    return null;
  }
}

function persist() {
  try {
    window.localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        consentId: state.consentId,
        preferences: state.preferences,
        policyVersion: state.policyVersion,
        consentDate: state.consentDate,
        hasDecided: state.hasDecided,
      }),
    );
  } catch {
    /* storage unavailable — ignore */
  }
  // Compact cookie so SSR / non-JS contexts can still read the decision.
  try {
    const compact = JSON.stringify({
      id: state.consentId,
      e: 1,
      a: state.preferences.analytics ? 1 : 0,
      m: state.preferences.marketing ? 1 : 0,
      p: state.preferences.preferences ? 1 : 0,
      v: state.policyVersion,
    });
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(compact)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function hydrate() {
  if (hydrationDone) return;
  hydrationDone = true;
  const stored = readLocalStorage();
  state = stored ?? { ...SERVER_STATE, consentId: genId(), hydrated: true };
  emit();
}

function subscribe(cb: () => void): () => void {
  hydrate();
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): ConsentState {
  return state;
}

function getServerSnapshot(): ConsentState {
  return SERVER_STATE;
}

// --- server sync (fire-and-forget) -----------------------------------------

async function syncToServer(method: ConsentMethod, isUpdate: boolean) {
  if (!state.consentId) return;
  try {
    await fetch("/api/lgpd/consent", {
      method: isUpdate ? "PUT" : "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify({
        consentId: state.consentId,
        analytics: state.preferences.analytics,
        marketing: state.preferences.marketing,
        preferences: state.preferences.preferences,
        method,
        policyVersion: state.policyVersion ?? "1.0",
        language: typeof navigator !== "undefined" ? navigator.language : undefined,
        timezone:
          typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
      }),
    });
  } catch {
    /* fire-and-forget — never block the UX */
  }
}

function commit(prefs: ConsentPreferences, policyVersion: string, method: ConsentMethod) {
  const wasDecided = state.hasDecided;
  state = {
    consentId: state.consentId ?? genId(),
    preferences: { ...prefs, essential: true },
    policyVersion,
    consentDate: new Date().toISOString(),
    hasDecided: true,
    hydrated: true,
  };
  persist();
  emit();
  void syncToServer(method, wasDecided);
}

// --- public actions --------------------------------------------------------

export function acceptAll(policyVersion: string, method: ConsentMethod = "banner") {
  commit(ALL_ON, policyVersion, method);
}

export function declineAll(policyVersion: string, method: ConsentMethod = "banner") {
  commit(ALL_OFF, policyVersion, method);
}

export function savePreferences(
  prefs: Partial<ConsentPreferences>,
  policyVersion: string,
  method: ConsentMethod = "modal",
) {
  commit({ ...state.preferences, ...prefs, essential: true }, policyVersion, method);
}

export function resetConsent() {
  state = { ...SERVER_STATE, consentId: genId(), hydrated: true };
  try {
    window.localStorage.removeItem(LS_KEY);
    document.cookie = `${COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    /* ignore */
  }
  emit();
}

// --- hooks -----------------------------------------------------------------

export function useConsent(): ConsentState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
